import { MutableRefObject, useEffect, useRef, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { Grid, Stack } from '@common-components';
import { CarrierModule, OrganizationType } from 'enums';
import { useFormProvider, useMutateOrganization } from 'hooks';
import { useMutateMarket } from 'hooks/api/market';
import { useMutateOrganizationDomain } from 'hooks/api/organization-domain';
import { messages } from 'i18n';
import { Market, OrganizationDomain } from 'types';
import { scrollToElement } from 'utils';
import { DialogContent, DialogFooter } from 'components/Dialog';
import { FormMultiSelectAutocomplete } from 'components/hookFormComponents/FormMultiSelectAutocomplete';
import FormTextField from 'components/hookFormComponents/FormTextField';
import { BaseRoutedDialogContentProps } from 'broker/components/RoutedDialog';
import { CarrierFormDataProps, CarrierFormFieldsNames } from './types';
import { formFieldsConfig, FormMode, schema } from './utils';

const carrierModuleOptions = Object.values(CarrierModule).map((value) => ({ label: value, value }));

const focusOnRef = (ref: MutableRefObject<HTMLInputElement | null>) => {
  setTimeout(() => {
    ref.current?.focus();
  }, 200);
};

interface FormContentProps
  extends Pick<BaseRoutedDialogContentProps, 'onClose' | 'closeWithoutPrompt' | 'setDialogIsDirty'> {
  defaultValues: CarrierFormDataProps;
  existingDomains: OrganizationDomain[];
  mode: FormMode;
  market: Market | null;
  scrollTo?: CarrierFormFieldsNames;
}

export default function FormContent({
  setDialogIsDirty,
  onClose,
  closeWithoutPrompt,
  market,
  existingDomains,
  defaultValues,
  mode,
  scrollTo,
}: FormContentProps) {
  const { methods } = useFormProvider({ defaultValues, schema, setIsDirty: setDialogIsDirty });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const domainRef = useRef<HTMLInputElement | null>(null);

  const { updateOrganization } = useMutateOrganization();
  const { createMarketByName, updateMarket } = useMutateMarket();
  const { deleteOrganizationDomain, createOrganizationDomain } = useMutateOrganizationDomain();

  const organizationId = market?.organizationId ?? '';
  const marketId = market?.id ?? '';

  useEffect(() => {
    if (scrollTo) {
      setTimeout(() => {
        scrollToElement(scrollTo, { block: 'center' });
      }, 100);
      if (scrollTo === CarrierFormFieldsNames.Domain) {
        focusOnRef(domainRef);
      }
    }
  }, [scrollTo]);

  const onSubmit = async (data: CarrierFormDataProps) => {
    const { name, domain, purchasedModules, stpConfidenceThreshold } = data;
    const domainFiltered = domain.filter((d) => d?.trim());

    setIsSubmitting(true);
    setDialogIsDirty(false);
    try {
      if (mode === FormMode.Update) {
        await updateOrganization.mutateAsync({
          id: organizationId,
          data: { name },
        });

        await updateMarket.mutateAsync({
          id: marketId,
          data: {
            purchasedModules: purchasedModules ?? [],
            stpConfidenceThreshold: stpConfidenceThreshold ?? null,
          },
        });

        const domainsToDelete = existingDomains.filter((d) => !domainFiltered.includes(d.domain));
        const domainsToAdd = domainFiltered.filter((d) => !existingDomains.map((ed) => ed.domain).includes(d));

        await Promise.all(domainsToDelete.map((d) => deleteOrganizationDomain.mutateAsync({ id: d.id })));

        await Promise.all(
          domainsToAdd.map((d) =>
            createOrganizationDomain.mutateAsync({
              data: {
                organizationId,
                domain: d,
              },
            }),
          ),
        );
      } else {
        const res = await createMarketByName.mutateAsync({
          data: {
            name,
            organizationType: OrganizationType.Carrier,
          },
        });

        if (res) {
          await updateMarket.mutateAsync({
            id: res.id,
            data: {
              purchasedModules: purchasedModules ?? [],
              stpConfidenceThreshold: stpConfidenceThreshold ?? null,
            },
          });

          await Promise.all(
            domainFiltered.map((d) =>
              createOrganizationDomain.mutateAsync({
                data: {
                  organizationId: res.organizationId,
                  domain: d,
                },
              }),
            ),
          );
        }
      }
      closeWithoutPrompt();
    } catch (e) {
      setDialogIsDirty(true);
      throw e;
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormProvider {...methods}>
      <DialogContent>
        <Stack maxWidth={640} margin="auto" gap={1}>
          <Grid columnSpacing={1} container>
            <Grid item xs={12}>
              <FormTextField {...formFieldsConfig[CarrierFormFieldsNames.Name]} fullWidth />
            </Grid>
            <Grid item xs={12}>
              <FormMultiSelectAutocomplete
                inputRef={domainRef}
                options={[]}
                hideOptionalLabel
                freeSolo
                fullWidth
                defaultValue={defaultValues.domain ?? []}
                {...formFieldsConfig[CarrierFormFieldsNames.Domain]}
                createOption={(value) => ({ label: `${messages.buttons.add}: ${value}`, value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormMultiSelectAutocomplete
                options={carrierModuleOptions}
                fullWidth
                defaultValue={defaultValues.purchasedModules ?? []}
                {...formFieldsConfig[CarrierFormFieldsNames.PurchasedModules]}
              />
            </Grid>
            <Grid item xs={12}>
              <FormTextField
                {...formFieldsConfig[CarrierFormFieldsNames.StpConfidenceThreshold]}
                fullWidth
                type="number"
                inputProps={{ min: 0, max: 1, step: 0.01 }}
              />
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>
      <DialogFooter
        proceedButton={{
          children: messages.carriersPage.carrierForm.submit[mode],
          onClick: methods.handleSubmit(onSubmit),
          loading: isSubmitting,
        }}
        cancelButton={{
          children: messages.buttons.cancel,
          onClick: onClose,
        }}
      />
    </FormProvider>
  );
}
